import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PhoneCall, CalendarClock, StickyNote } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import LoadingSpinner from '../common/LoadingSpinner';

function tipoBadgeClass(tipo) {
    switch (tipo) {
        case 'chiamata':
            return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
        case 'messaggio':
            return 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200';
        case 'invito_allenamento':
            return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
        case 'modifica_scheda':
            return 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100';
        case 'colloquio':
            return 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200';
        default:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-white/90';
    }
}

/** Tempo relativo (es. «2 giorni fa») in base alla lingua UI. */
function formatRelativeAgo(createdAt, lang) {
    const d = new Date(createdAt.replace(' ', 'T'));
    const now = new Date();
    const diffSec = Math.floor((now.getTime() - d.getTime()) / 1000);
    if (diffSec < 0) return null;
    const loc = lang.startsWith('en') ? 'en' : 'it';
    const rtf = new Intl.RelativeTimeFormat(loc, { numeric: 'auto' });
    const mins = Math.floor(diffSec / 60);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);
    if (days < 1) {
        if (hours < 1) {
            if (mins < 1) return rtf.format(-diffSec, 'second');
            return rtf.format(-mins, 'minute');
        }
        return rtf.format(-hours, 'hour');
    }
    if (days < 60) return rtf.format(-days, 'day');
    const months = Math.floor(days / 30);
    if (months < 24) return rtf.format(-months, 'month');
    return rtf.format(-Math.floor(days / 365), 'year');
}

function formatDateTimeLong(createdAt, locale) {
    const d = new Date(createdAt.replace(' ', 'T'));
    return d.toLocaleString(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

/**
 * Storico azioni da «Clienti da contattare» (tabella gym_contact_log, API gym_dashboard contact_log).
 */
export default function ClientContactLogTab({ memberId, embedded = false }) {
    const { getAuthenticatedApi } = useAuth();
    const { t, i18n } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 1023px)');
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const memberIdRef = useRef(memberId);
    memberIdRef.current = memberId;

    const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'it-IT';

    const labelForTipo = useCallback(
        (tipo) => {
            const map = {
                chiamata: 'chiamata',
                messaggio: 'messaggio',
                invito_allenamento: 'invitoAllenamento',
                modifica_scheda: 'modificaScheda',
                colloquio: 'colloquio'
            };
            const key = map[tipo];
            return key ? t(`gym.dashboard.daContattareModal.${key}`) : tipo;
        },
        [t]
    );

    const fetchLog = useCallback(async () => {
        const requestedId = memberId;
        setLoading(true);
        setError(null);
        try {
            const api = getAuthenticatedApi();
            const res = await api.get(`/gym_dashboard.php?action=contact_log&user_id=${requestedId}`);
            if (memberIdRef.current !== requestedId) return;
            if (res.data?.success && Array.isArray(res.data.entries)) {
                setEntries(res.data.entries);
            } else {
                setEntries([]);
            }
        } catch (e) {
            if (memberIdRef.current !== requestedId) return;
            console.error('Errore caricamento contact log:', e);
            setError(t('gym.membersManager.contactLog.loadError'));
            setEntries([]);
        } finally {
            if (memberIdRef.current === requestedId) {
                setLoading(false);
            }
        }
    }, [getAuthenticatedApi, memberId, t]);

    useEffect(() => {
        fetchLog();
    }, [fetchLog]);

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {!embedded && (
                <div>
                    <h3 className={`font-semibold text-gray-900 dark:text-white ${isMobile ? 'text-base' : 'text-lg'}`}>
                        {t('gym.membersManager.contactLog.title')}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-white/65">{t('gym.membersManager.contactLog.subtitle')}</p>
                    {entries.length > 0 && (
                        <p className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-white/55">{t('gym.membersManager.contactLog.notesHint')}</p>
                    )}
                </div>
            )}
            {embedded && entries.length > 0 && (
                <p className="text-xs leading-relaxed text-gray-500 dark:text-white/55">{t('gym.membersManager.contactLog.notesHint')}</p>
            )}
            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            {!error && entries.length === 0 && (
                <div className="rounded-xl border border-dashed border-gray-200 py-10 text-center dark:border-white/15">
                    <PhoneCall className="mx-auto mb-2 h-10 w-10 text-gray-400 dark:text-white/40" aria-hidden />
                    <p className="text-sm text-gray-500 dark:text-white/65">{t('gym.membersManager.contactLog.empty')}</p>
                </div>
            )}
            {entries.length > 0 && (
                <ul
                    className={`divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200 bg-white dark:divide-white/10 dark:border-white/10 dark:bg-gray-900/80 ${isMobile ? 'text-sm' : ''}`}
                >
                    {entries.map((row) => {
                        const hasNote = row.note != null && String(row.note).trim() !== '';
                        const rel = row.created_at ? formatRelativeAgo(row.created_at, i18n.language || 'it') : null;
                        return (
                            <li key={row.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <span
                                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${tipoBadgeClass(row.tipo_azione)}`}
                                    >
                                        {labelForTipo(row.tipo_azione)}
                                    </span>
                                </div>
                                {row.created_at && (
                                    <div className="flex flex-col gap-0.5 text-sm text-gray-600 dark:text-white/80">
                                        <div className="flex items-start gap-2">
                                            <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-white/50" aria-hidden />
                                            <div>
                                                <p className="text-gray-900 dark:text-white font-medium">
                                                    {formatDateTimeLong(row.created_at, locale)}
                                                </p>
                                                {rel && (
                                                    <p className="mt-0.5 text-xs text-gray-500 dark:text-white/55">{rel}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="mt-3 border-t border-gray-100 pt-3 dark:border-white/10">
                                    <div className="flex items-start gap-2">
                                        <StickyNote className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-white/50" aria-hidden />
                                        <div className="min-w-0 flex-1">
                                            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-white/55">
                                                {t('gym.membersManager.contactLog.notesLabel')}
                                            </p>
                                            {hasNote ? (
                                                <p className="whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-white/90">{row.note}</p>
                                            ) : (
                                                <p className="text-sm italic text-gray-400 dark:text-white/45">{t('gym.membersManager.contactLog.notesEmpty')}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
